import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, AlertTriangle, Pencil } from "lucide-react";
import ConfirmDelete from "@/components/ConfirmDelete";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";

export default function Reviews() {
  const { branch, user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ performance_score: 3, engagement_score: 3 });

  const load = async () => {
    const [r, w, e] = await Promise.all([
      api.get("/reviews").catch(() => ({ data: [] })),
      api.get("/reviews/watchlist").catch(() => ({ data: [] })),
      api.get("/employees").catch(() => ({ data: [] })),
    ]);
    setReviews(r.data);
    setWatchlist(w.data);
    setEmployees(e.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = reviews.filter((r) => branch === "All" || r.branch === branch);

  const save = async () => {
    try {
      const { id, ...body } = form;
      if (id) {
        await api.patch(`/reviews/${id}`, body);
        toast.success("Review updated");
      } else {
        await api.post("/reviews", body);
        toast.success("Review logged");
      }
      setOpen(false);
      setForm({ performance_score: 3, engagement_score: 3 });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const canManage = ["super_admin", "hr_manager", "branch_manager"].includes(user?.role);

  const openEdit = (r) => {
    setForm({
      id: r.id, employee_id: r.employee_id,
      review_date: r.review_date?.slice(0, 10) || "",
      next_review_date: r.next_review_date?.slice(0, 10) || "",
      performance_score: r.performance_score ?? 3,
      engagement_score: r.engagement_score ?? 3,
      key_points: r.key_points || "",
      action_plan: r.action_plan || "",
    });
    setOpen(true);
  };

  const remove = async (id) => {
    try {
      await api.delete(`/reviews/${id}`);
      toast.success("Review deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const riskCls = (r) => ({ High: "rag-red", Medium: "rag-amber", Low: "rag-green" }[r] || "rag-amber");

  return (
    <div className="space-y-6" data-testid="reviews-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-eyebrow">Retention &amp; Performance</div>
          <h1 className="font-heading text-3xl font-bold text-[var(--yc-navy)] tracking-tight mt-1">Reviews &amp; Risk Watchlist</h1>
          <p className="text-slate-600 text-sm mt-1">Retention risk auto-computed from scores + satisfaction.</p>
        </div>
        {canManage && (
          <Button onClick={() => { setForm({ performance_score: 3, engagement_score: 3 }); setOpen(true); }} className="rounded-full bg-[var(--yc-navy)] hover:bg-[var(--yc-navy-soft)]" data-testid="add-review-button">
            <Plus className="h-4 w-4 mr-1" /> Log review
          </Button>
        )}
      </div>

      {/* Watchlist */}
      <div className="bg-white border border-slate-200 rounded-lg">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
          <AlertTriangle className="h-4 w-4 text-[var(--yc-rose)]" />
          <div>
            <div className="label-eyebrow">Auto-compiled</div>
            <div className="font-heading font-semibold text-[var(--yc-navy)]">High-Risk Watchlist</div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-100 bg-slate-50">
                <th className="data-cell">Employee</th>
                <th className="data-cell">Dept · Branch</th>
                <th className="data-cell">Satisfaction</th>
                <th className="data-cell">Last review</th>
                <th className="data-cell">Next review</th>
                <th className="data-cell">Risk</th>
              </tr>
            </thead>
            <tbody>
              {watchlist.length === 0 && (
                <tr><td colSpan={6} className="data-cell text-center text-slate-500 py-6">No high-risk employees currently.</td></tr>
              )}
              {watchlist.map((w) => (
                <tr key={w.employee_id} className="border-b border-slate-100 data-row" data-testid={`watchlist-row-${w.employee_id}`}>
                  <td className="data-cell font-medium text-[var(--yc-navy)]">{w.name}</td>
                  <td className="data-cell text-slate-600">{w.department} · {w.branch}</td>
                  <td className="data-cell text-slate-600">{w.satisfaction_status}</td>
                  <td className="data-cell text-slate-500">{w.last_review_date?.slice(0,10) || "—"}</td>
                  <td className="data-cell text-slate-500">{w.next_review_date?.slice(0,10) || "—"}</td>
                  <td className="data-cell"><span className="rag-red px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold">High</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Review log */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100">
          <div className="label-eyebrow">Review log</div>
          <div className="font-heading font-semibold text-[var(--yc-navy)]">All performance conversations</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-100 bg-slate-50">
                <th className="data-cell">Employee</th>
                <th className="data-cell">Dept · Branch</th>
                <th className="data-cell">Date</th>
                <th className="data-cell">Reviewer</th>
                <th className="data-cell">Perf</th>
                <th className="data-cell">Eng</th>
                <th className="data-cell">Risk</th>
                <th className="data-cell">Next</th>
                <th className="data-cell">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="data-cell text-center text-slate-500 py-8">No reviews logged yet.</td></tr>
              )}
              {filtered.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 data-row">
                  <td className="data-cell font-medium text-[var(--yc-navy)]">{r.employee_name}</td>
                  <td className="data-cell text-slate-600">{r.department} · {r.branch}</td>
                  <td className="data-cell">{r.review_date?.slice(0,10)}</td>
                  <td className="data-cell text-slate-600">{r.reviewer}</td>
                  <td className="data-cell font-mono">{r.performance_score}</td>
                  <td className="data-cell font-mono">{r.engagement_score}</td>
                  <td className="data-cell"><span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold ${riskCls(r.retention_risk)}`}>{r.retention_risk}</span></td>
                  <td className="data-cell text-slate-500">{r.next_review_date?.slice(0,10)}</td>
                  <td className="data-cell">
                    <div className="flex items-center gap-1">
                      {canManage && (
                        <>
                          <button type="button" onClick={() => openEdit(r)} className="p-1.5 rounded-md text-slate-400 hover:text-[var(--yc-navy)] hover:bg-slate-100 transition-colors" title="Edit" data-testid={`edit-review-${r.id}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <ConfirmDelete onConfirm={() => remove(r.id)} label={`review for ${r.employee_name}`} testId={`delete-review-${r.id}`} />
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">{form.id ? "Edit performance review" : "Log performance review"}</DialogTitle>
            <DialogDescription>Score performance and engagement — retention risk is auto-computed.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label className="label-eyebrow">Employee</Label>
              <Select value={form.employee_id || ""} onValueChange={(v) => setForm({ ...form, employee_id: v })} disabled={!!form.id}>
                <SelectTrigger className="mt-1.5" data-testid="review-employee-select"><SelectValue placeholder="Choose employee" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} — {e.department}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-eyebrow">Review date</Label>
              <Input type="date" value={form.review_date || ""} onChange={(e) => setForm({ ...form, review_date: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label className="label-eyebrow">Next review</Label>
              <Input type="date" value={form.next_review_date || ""} onChange={(e) => setForm({ ...form, next_review_date: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label className="label-eyebrow">Performance ({form.performance_score}/5)</Label>
              <Input type="range" min={1} max={5} step={0.5} value={form.performance_score} onChange={(e) => setForm({ ...form, performance_score: Number(e.target.value) })} className="mt-3" />
            </div>
            <div>
              <Label className="label-eyebrow">Engagement ({form.engagement_score}/5)</Label>
              <Input type="range" min={1} max={5} step={0.5} value={form.engagement_score} onChange={(e) => setForm({ ...form, engagement_score: Number(e.target.value) })} className="mt-3" />
            </div>
            <div className="col-span-2">
              <Label className="label-eyebrow">Key discussion points</Label>
              <Textarea rows={2} value={form.key_points || ""} onChange={(e) => setForm({ ...form, key_points: e.target.value })} className="mt-1.5" />
            </div>
            <div className="col-span-2">
              <Label className="label-eyebrow">Action plan</Label>
              <Textarea rows={2} value={form.action_plan || ""} onChange={(e) => setForm({ ...form, action_plan: e.target.value })} className="mt-1.5" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[var(--yc-navy)] hover:bg-[var(--yc-navy-soft)]" data-testid="save-review-button">{form.id ? "Save changes" : "Save review"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
